import React, { useEffect, useState, useContext } from 'react';
import TeamCard from './TeamCard';
import ModernCarousel from './ModernCarousel';
import AuthContext from '../context/AuthContext';
import UnifiedLoader from './UnifiedLoader';
import { ArrowRightIcon, ChevronRightIcon } from './Icons';

const TeamSection = ({ header }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchPublicData } = useContext(AuthContext);

  const title = header?.title || 'Unser Team';
  const subtitle = header?.subtitle || 'Lerne die Menschen hinter dem Projekt kennen, die mit Herzblut und Erfahrung unsere Abenteuer begleiten.';

  // Expansion and Pagination State
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9); // Default 3 rows of 3 on desktop

  // Fetch team members from API
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        // Fetch all team members to support expansion
        const data = await fetchPublicData('/team');
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => {
            const vA = a.views || 0;
            const vB = b.views || 0;
            if (vB !== vA) return vB - vA;
            return (a.order || 0) - (b.order || 0);
          });
          setTeamMembers(sorted);
        } else {
          // Fallback data if API returns empty or invalid
          setHarcodedTeamMembers();
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
        setHarcodedTeamMembers();
      } finally {
        setLoading(false);
      }
    };

    const setHarcodedTeamMembers = () => {
      setTeamMembers([
        {
          name: 'Devino',
          role: 'Projektleitung',
          description: 'Hey, ich bin Devino, 18 Jahre alt und leite unser Projekt. Ich freue mich mit meinen Freunden eine Pfadfindergruppe hier in Wutöschingen aufzubauen und gemeinsam mit euch Abenteuer zu erleben.',
          photos: [{ url: 'https://i.imgur.com/wV1lrNW.jpeg', isProfilePhoto: true }]
        },
        {
          name: 'Lena',
          role: 'Teamerin',
          description: 'Ich bin Lena, 17 Jahre alt, und habe bei den Pfadis jede Menge Spaß. Mir gefällt besonders, dass wir viel zusammen machen und draußen immer neue Abenteuer erleben.',
          photos: [{ url: `https://ui-avatars.com/api/?name=Lena&background=random&size=500`, isProfilePhoto: true }]
        },
        {
          name: 'Maike',
          role: 'Teamerin',
          description: 'Hey ich bin Maike und bin 17 Jahre alt. Ich mag es gemeinsam draußen unterwegs zu sein-mit Rucksack und guter Stimmung. Ich freu mich auf viele Abenteuer mit euch!',
          photos: [{ url: 'https://i.imgur.com/NzIv6iv.jpg', isProfilePhoto: true }]
        },
      ]);
    }

    fetchTeamMembers();
  }, [fetchPublicData]);

  const handleLoadMore = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    } else {
      setVisibleCount(prev => prev + 9); // Load 3 more rows
    }
  };

  if (loading) {
    return (
      <section id="team-section" className="py-16 md:py-24 bg-surface-container scroll-mt-28">
        <div className="container mx-auto px-4">
          <UnifiedLoader text="Lade Team..." />
        </div>
      </section>
    );
  }

  const hasMore = isExpanded ? teamMembers.length > visibleCount : teamMembers.length > 3;

  return (
    <section id="team-section" className="py-16 md:py-24 bg-surface-container scroll-mt-28">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-end mb-12">
          <div className="space-y-2">
            <h2 className="text-headline-large md:text-display-small font-bold text-surface-on">
              {title}
            </h2>
            <p className="text-body-large text-surface-onVariant max-w-2xl">
              {subtitle}
            </p>
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="md3-btn-icon-unfilled w-12 h-12 flex items-center justify-center rounded-full bg-surface-containerHigh text-surface-on hover:bg-primary/10 hover:text-primary transition-all duration-300 shadow-elevation-1 cursor-pointer"
            aria-label={isExpanded ? "Sektion einklappen" : "Sektion ausklappen"}
          >
            <ChevronRightIcon
              className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
              sx={{ fontSize: 32 }}
            />
          </button>
        </div>

        {/* Dynamic View: Carousel or Grid */}
        <div className="transition-all duration-500 ease-in-out">
          {!isExpanded ? (
            <ModernCarousel animated={true}>
              {teamMembers.map((member, index) => {
                const profilePhotoUrl = typeof member.profilePhoto === 'string'
                  ? member.profilePhoto
                  : (member.profilePhoto?.url || member.photos?.find(p => p && (p.isProfilePhoto || p.url))?.url || (typeof member.photos?.[0] === 'string' ? member.photos[0] : member.photos?.[0]?.url));
                return (
                  <TeamCard
                    key={member._id || index}
                    member={member}
                    image={profilePhotoUrl}
                    name={member.name}
                    description={member.description}
                  />
                );
              })}
            </ModernCarousel>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
              {teamMembers.slice(0, visibleCount).map((member, index) => {
                const profilePhotoUrl = typeof member.profilePhoto === 'string'
                  ? member.profilePhoto
                  : (member.profilePhoto?.url || member.photos?.find(p => p && (p.isProfilePhoto || p.url))?.url || (typeof member.photos?.[0] === 'string' ? member.photos[0] : member.photos?.[0]?.url));
                return (
                  <div key={member._id || index} className="h-full">
                    <TeamCard
                      member={member}
                      image={profilePhotoUrl}
                      name={member.name}
                      description={member.description}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {hasMore && (
          <div className="mt-16 text-center">
            <button
              onClick={handleLoadMore}
              className="md3-btn-filled rounded-full h-14 px-12 gap-3 shadow-elevation-2 hover:shadow-elevation-4 hover:scale-105 transition-all group"
            >
              {isExpanded ? 'Noch mehr laden' : 'Alle Teammitglieder anzeigen'}
              <ArrowRightIcon className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'}`} style={{ fontSize: 20 }} />
            </button>
          </div>
        )}

        {isExpanded && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsExpanded(false)}
              className="md3-btn-text text-surface-onVariant hover:text-primary transition-colors font-medium"
            >
              Weniger anzeigen
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TeamSection;
